import { useRef, useLayoutEffect, useCallback, useEffect, useMemo } from 'react';
import styles from './index.module.scss';
import { readWrapper } from './utils';
import { useThrottleFn, useMemoizedFn } from 'ahooks';

interface EmailContentViewerProps {
  content: string;
  onContentLoaded?: () => void;
  onHeightChange?: (height: number) => void;
  visible?: boolean;
  contentId?: string; // 用于生成稳定的key
}

// 定义消息接口
interface IframeMessage {
  name: string;
  value: unknown;
}

export const EmailContentViewerV2 = (props: EmailContentViewerProps) => {
  const { content, onContentLoaded, onHeightChange, visible = true, contentId } = props;
  const frameRef = useRef<HTMLIFrameElement>(null);

  // 使用 useMemoizedFn 创建稳定的回调函数，内部判断是否存在
  const handleContentLoaded = useMemoizedFn(() => {
    onContentLoaded?.();
  });

  const handleHeightChange = useMemoizedFn((height: number) => {
    onHeightChange?.(height);
  });

  // 重设尺寸
  const resetIframeSize = useCallback((): void => {
    if (frameRef.current?.contentDocument) {
      const doc = frameRef.current.contentDocument;
      const bodyElements = doc.getElementsByTagName('body');
      const realCont = bodyElements?.length ? bodyElements[0] : null;
      // 计算content的最大高度
      const contentHeight = realCont?.scrollHeight || 0;

      frameRef.current.style.height = contentHeight ? contentHeight + 2 + 'px' : 'auto';

      // 通知外部高度变化
      if (contentHeight > 0) {
        handleHeightChange(contentHeight + 2);
      }
    }
  }, [handleHeightChange]);

  // 节流重设iframe尺寸
  const { run: throttleResetIframeSize } = useThrottleFn(resetIframeSize, {
    wait: 300,
    leading: true,
    trailing: true,
  });

  useLayoutEffect(() => {
    if (frameRef.current && content && visible) {
      // 清除iframe上的样式-宽度等，因为内容变化后需要重新计算
      frameRef.current.setAttribute('style', '');
      // 加载正文
      const doc = frameRef.current.contentDocument;
      if (doc) {
        // 主动重置滚动条位置
        doc.documentElement.scrollLeft = 0;
      }
      // 清除正文内容并写入
      doc?.open();
      doc?.write(readWrapper(content));
      doc?.close();
      // 开启iframe自动尺寸调节
      throttleResetIframeSize();
    }
  }, [content, visible, throttleResetIframeSize]);

  /**
   * 读信页-content iframe中的业务消息处理
   */
  const messageHandlesMap: Record<string, (value: unknown) => void> = useMemo(() => {
    return {
      readMail_resizeSize: (_value: unknown) => {
        throttleResetIframeSize();
      },
      readMail_mailLoadOver: (_value: unknown) => {
        handleContentLoaded();
        // 内容加载完成后也重新计算一次高度
        throttleResetIframeSize();
      },
    };
  }, [throttleResetIframeSize, handleContentLoaded]);

  // 处理iframe的postMessage消息
  const handleMessage = useCallback(
    (event: MessageEvent<IframeMessage>) => {
      //  限制消息的来源,只处理当前iframe内的消息
      if (frameRef.current?.contentWindow === event?.source) {
        const { data } = event;
        const { name, value } = data || { name: '', value: undefined };
        if (name && messageHandlesMap[name]) {
          messageHandlesMap[name](value);
        }
      }
    },
    [messageHandlesMap]
  );

  /**
   * 监听iframe内的消息通知
   */
  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  if (!visible) {
    return null;
  }

  return (
    <div className={styles.emailContentWrap}>
      {content ? (
        <iframe
          key={contentId || content}
          ref={frameRef}
          width="100%"
          height="100%"
          src="about:blank"
          frameBorder="0"
          title="Email content"
        />
      ) : (
        <span className="text-sm font-normal text-[#A3A3A3]">No Text Content</span>
      )}
    </div>
  );
};
